import { Box, Spinner, Table } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { TimeTrackingAttendaceTableWorkedBreaksDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import usePagination from 'modules/core/hooks/usePagination'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useGetAttendanceIncidentAggregateShiftLocations } from 'modules/attendance/hooks/shift_locations/useGetAggreagteShiftLocations'
import { Inconsistencies } from 'modules/attendance/hooks/useShiftInconsistencies'
import {
  Cycle,
  useAttendanceDateNavigator,
} from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { periodEmployee } from 'modules/attendance/lib/periodEmployee'
import { PeriodState } from 'modules/attendance/lib/types'
import type { ClockInStatus } from 'modules/attendance/pages/CustomPeriodsPage'
import type { Status } from 'modules/attendance/store/models/Period'
import type Period from 'modules/attendance/store/models/Period'
import employees from 'modules/employees/store/collections/employees'

import type CustomPeriodForm from '../../../Layout/form'
import { colsKeyType, getColumnsColspan } from '../tableColSpans'
import Item from './_Item'
import useData from './useData'

const BATCH_SIZE = 50

interface Props {
  selected: Period[]
  selectable: boolean
  compensatedAt: string
  onSelectAll: () => void
  customPeriods: Period[]
  currentTabState: Status
  onDeselectAll: () => void
  loadingPresences: boolean
  canSendEmailReminder: boolean
  compensatedEmployeeIds: number[]
  clockInStatuses: ClockInStatus[]
  customPeriodForm: CustomPeriodForm
  showHourDistributionColumn: boolean
  onToggleRange?: (item: Period) => void
  onToggleSelectedItem?: (item: Period) => void
  setPayrollCompensation: (arg: boolean) => void
  inconsistencies: Map<string | number, Inconsistencies>
}

const Body = ({
  selected,
  selectable,
  customPeriods,
  onToggleRange,
  compensatedAt,
  currentTabState,
  inconsistencies,
  loadingPresences,
  customPeriodForm,
  canSendEmailReminder,
  onToggleSelectedItem,
  setPayrollCompensation,
  compensatedEmployeeIds,
  showHourDistributionColumn,
  clockInStatuses,
}: Props) => {
  const { paginatedItems, loadMore, hasMore, page } = usePagination({
    batchSize: BATCH_SIZE,
    items: customPeriods,
  })

  const { featureAvailability: hasGeofencingFeature } = useAttendanceFeatures(
    Features.time_tracking_geofencing_alerts
  )

  const employeeIds = paginatedItems.slice(-BATCH_SIZE).map((item) => item.attributes.employee_id)

  const { stringDateFrom, stringDateTo, cycle } = useAttendanceDateNavigator()
  const from = stringDateFrom
  const to = stringDateTo
  const isMonthly = cycle == Cycle.monthly

  const { attendanceSettings, overtimeRequestsCollection, breakdowns } = useData({
    employeeIds,
    isMonthly,
    from,
    to,
    compensatedEmployeeIds,
    compensatedAt,
  })

  const { data } = useQuery(TimeTrackingAttendaceTableWorkedBreaksDocument, {
    variables: { startOn: from, endOn: to, employeeIds },
  })

  const workedBreaks = data?.attendance.workedBreaksConnection.nodes ?? []

  const employeesWithShiftLocations = employees
    .filter(
      (employee) =>
        employee.attributes.attendance_employees_policy_id &&
        attendanceSettings
          .byPolicy(employee.attributes.attendance_employees_policy_id)
          .some((attendanceSetting) => attendanceSetting.hasGeofencingEnabled)
    )
    .map((employee) => employee.id)

  const { aggregateShiftLocations: aggregateIncidentShiftLocations } =
    useGetAttendanceIncidentAggregateShiftLocations(
      {
        employeeIds: employeesWithShiftLocations,
        startOn: from,
        endOn: to,
      },
      employeesWithShiftLocations.length <= 0
    )

  const { featureAvailability: hasTimeTrackingInconsistencies } = useAttendanceFeatures(
    Features.time_tracking_time_alerts
  )
  const COLSPANS = getColumnsColspan()

  const columnNames = Object.keys(COLSPANS)

  const totalColSpans = columnNames.reduce((sum, value, i) => {
    if (
      (columnNames[i] === 'ALERTS_COLUMN' && !hasTimeTrackingInconsistencies) ||
      (columnNames[i] === 'DISTRIBUTION_COLUMN' && !showHourDistributionColumn) ||
      (columnNames[i] === 'SELECT_ALL_COLUMN' && !selectable)
    ) {
      return sum
    }
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    return sum + (COLSPANS[value as colsKeyType] ?? 0)
  }, 0)

  const blankText = (state: Status): string => {
    switch (state) {
      case PeriodState.approved:
        return i18n.t('time_tracking.blank_slate.approved')

      case PeriodState.in_progress:
        return i18n.t('time_tracking.blank_slate.in_progress')

      case PeriodState.pending:
        return i18n.t('time_tracking.blank_slate.pending')

      default:
        return ''
    }
  }

  useEffect(() => {
    setPayrollCompensation(attendanceSettings.hasBalancePayrollCompensation())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceSettings.hasBalancePayrollCompensation()])

  useEffect(() => {
    if (!hasMore) track(trackingEvents.timeTracking.endLoadingTimeTrackingView, {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loadingPresences)
    return (
      <Table.Body>
        <Table.Row>
          <Table.Cell colSpan={totalColSpans} wide>
            <Box width="full" alignItems="center">
              <Spinner />
            </Box>
          </Table.Cell>
        </Table.Row>
      </Table.Body>
    )

  if (!customPeriods.length) {
    return (
      <Table.Body>
        <Table.Blank colSpan={totalColSpans} text={blankText(currentTabState)} />
      </Table.Body>
    )
  }

  return (
    <Table.BodyWithInfiniteScroll
      page={page}
      loadMore={loadMore}
      hasMore={hasMore}
      colSpan={totalColSpans}
    >
      {paginatedItems.map((customPeriod) => {
        const { employee_id } = customPeriod.attributes
        const employeeInconsistencies =
          hasTimeTrackingInconsistencies && inconsistencies.has(employee_id)
            ? inconsistencies.get(employee_id)
            : undefined

        const clockInStatus = clockInStatuses.find(
          (employeeClockInStatus) => employeeClockInStatus.employee?.id === employee_id
        )

        return (
          <Item
            key={customPeriod.id}
            customPeriod={customPeriod}
            workedBreaks={workedBreaks.filter(
              (workedBreak) => workedBreak.employeeId === employee_id
            )}
            attendanceSettings={attendanceSettings.byPolicy(
              periodEmployee(customPeriod, employees).attributes.attendance_employees_policy_id ?? 0
            )}
            overtimeRequests={overtimeRequestsCollection.byEmployee(employee_id)}
            aggregateIncidentShiftLocation={aggregateIncidentShiftLocations.find(
              (shiftLocation) => shiftLocation.employeeId === employee_id
            )}
            balanceBreakdown={breakdowns?.find((breakdown) => breakdown.isValidFor(customPeriod))}
            onToggleRange={(...args) => onToggleRange && onToggleRange(...args)}
            onToggleSelectedItem={(...args) =>
              onToggleSelectedItem && onToggleSelectedItem(...args)
            }
            selected={selected}
            customPeriodForm={customPeriodForm}
            hasGeofencingFeatureFlag={hasGeofencingFeature}
            showHourDistributionColumn={showHourDistributionColumn}
            canSendEmailReminder={canSendEmailReminder}
            inconsistencies={employeeInconsistencies}
            clockInStatus={clockInStatus}
          />
        )
      })}
    </Table.BodyWithInfiniteScroll>
  )
}

export default observer(Body)
